import React, { useState } from "react";

/**
 * Multi-pick "Activity + Event Attribute" list, used for the actual (Ist-
 * Termin) date when its source is 'event_attribute'. Mirrors the existing
 * multi-select "earliest wins" behaviour for object_attribute/activity_timestamp
 * (see DeliveryDateConfigSection), but each candidate here needs BOTH an
 * activity and an attribute name, so entries are added one pair at a time via
 * a small draft picker instead of a flat toggle-list.
 */
const EventAttributeDateSourceListPicker = ({
  values,
  onChange,
  activitiesForType,
  getAttributesForActivity,
  loading = false,
  activitiesLoading = false,
}) => {
  const [draftActivity, setDraftActivity] = useState("");
  const [draftAttribute, setDraftAttribute] = useState("");
  const draftAttrs = draftActivity ? getAttributesForActivity(draftActivity) : [];

  const addPair = () => {
    if (!draftActivity || !draftAttribute) return;
    const exists = values.some((v) => v.activity === draftActivity && v.attribute === draftAttribute);
    if (!exists) onChange([...values, { activity: draftActivity, attribute: draftAttribute }]);
    setDraftActivity("");
    setDraftAttribute("");
  };

  const removePair = (idx) => onChange(values.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v, i) => (
            <span
              key={`${v.activity}::${v.attribute}::${i}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 bg-primary-50 text-primary-700 rounded text-xs"
            >
              {v.activity} → {v.attribute}
              <button
                type="button"
                onClick={() => removePair(i)}
                disabled={loading}
                className="text-primary-500 hover:text-primary-800"
                aria-label="Entfernen"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <select
          value={draftActivity}
          onChange={(e) => { setDraftActivity(e.target.value); setDraftAttribute(""); }}
          disabled={loading || activitiesLoading}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">Aktivität…</option>
          {activitiesForType.map((act) => (
            <option key={act} value={act}>{act}</option>
          ))}
        </select>
        <select
          value={draftAttribute}
          onChange={(e) => setDraftAttribute(e.target.value)}
          disabled={loading || !draftActivity}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {!draftActivity ? (
            <option value="">Erst Aktivität wählen…</option>
          ) : draftAttrs.length === 0 ? (
            <option value="">Keine Event-Attribute gefunden</option>
          ) : (
            <option value="">Event-Attribut…</option>
          )}
          {draftAttrs.map((attr) => (
            <option key={attr} value={attr}>{attr}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={addPair}
          disabled={loading || !draftActivity || !draftAttribute}
          className="px-3 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        >
          + Hinzufügen
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-1">Mehrere Paare möglich. Frühestes Datum wird verwendet.</p>
    </div>
  );
};

export default EventAttributeDateSourceListPicker;
